import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
    useTheme,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'

import {
    Container,
    EzosLoader,
    MessageForm,
    SideDrawer,
    SideDrawerContainer,
} from '../../components'
import { useSupportTicket } from '../../hooks/useSupportTicket'
import { SupportTicketRow } from './SupportTicketRow'
import {
    SupportTicket,
    SupportTicketCategory,
    SupportTicketStatus,
    SupportTicketSubCategory,
} from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'
import { SupportTicketStatusFilterValue } from '../../hooks/useSupportTicket'

const DRAWER_W = 420

const normalizeCategory = (value: string): string =>
    value
        .split('_')
        .map(
            (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ')

const DEBOUNCE_MS = 350

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const

export const SupportTicketHome = () => {
    const {
        supportTicketList,
        supportTicketListData,
        listFilters,
        listParams,
        updateFilters,
        updateListParams,
        listBodyLoading,
        listError,
        updateSupportTicket,
        refetchSupportTicketList,
        bumpMessageCountForTicket,
    } = useSupportTicket({ listQueryEnabled: true })

    const [draftProperty, setDraftProperty] = useState(listFilters.propertyInfo)
    const [draftUser, setDraftUser] = useState(listFilters.userSearch)
    const [draftIssue, setDraftIssue] = useState(listFilters.issueSearch)

    useEffect(() => {
        const id = window.setTimeout(() => {
            updateFilters({
                propertyInfo: draftProperty,
                userSearch: draftUser,
                issueSearch: draftIssue,
            })
        }, DEBOUNCE_MS)
        return () => window.clearTimeout(id)
    }, [draftProperty, draftUser, draftIssue, updateFilters])

    const [openMessageDrawer, setOpenMessageDrawer] = useState(false)
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
        null,
    )

    const theme = useTheme()

    const totalCount = supportTicketListData?.count ?? 0
    const page = listParams.page
    const pageSize = listParams.pageSize

    const { rangeStart, rangeEnd, numPages } = useMemo(() => {
        if (totalCount === 0) {
            return { rangeStart: 0, rangeEnd: 0, numPages: 1 }
        }
        const start = (page - 1) * pageSize + 1
        const end = Math.min(page * pageSize, totalCount)
        return {
            rangeStart: start,
            rangeEnd: end,
            numPages: Math.max(1, Math.ceil(totalCount / pageSize)),
        }
    }, [totalCount, page, pageSize])

    const handleTransition = useCallback(
        async (
            e: React.MouseEvent<HTMLButtonElement>,
            ticket: SupportTicket,
            status: SupportTicketStatus,
        ) => {
            await updateSupportTicket(ticket, status)
        },
        [updateSupportTicket],
    )

    const handleOpenMessageDrawer = (ticket: SupportTicket) => {
        setSelectedTicket(ticket)
        setOpenMessageDrawer(true)
    }

    return (
        <SideDrawerContainer open={openMessageDrawer} width={DRAWER_W}>
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    padding: theme.spacing(1),
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                            color: theme.palette.darkGreen.main,
                        }}
                    >
                        Support Tickets
                    </span>
                </Container>

                <Container
                    style={{
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: theme.spacing(1.5),
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 140, flexShrink: 0 }}
                    >
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={listFilters.status}
                            onChange={(e) => {
                                updateFilters({
                                    status: e.target
                                        .value as SupportTicketStatusFilterValue,
                                })
                            }}
                            label="Status"
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value={SupportTicketStatus.OPEN}>
                                Open
                            </MenuItem>
                            <MenuItem value={SupportTicketStatus.IN_PROGRESS}>
                                In Progress
                            </MenuItem>
                            <MenuItem value={SupportTicketStatus.PAUSE}>
                                Paused
                            </MenuItem>
                            <MenuItem value={SupportTicketStatus.COMPLETE}>
                                Complete
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Container
                        style={{
                            flex: 1,
                            minWidth: 200,
                            flexWrap: 'wrap',
                            alignItems: 'flex-end',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <TextField
                            label="Property search"
                            variant="outlined"
                            value={draftProperty}
                            onChange={(e) => setDraftProperty(e.target.value)}
                            size="small"
                            fullWidth
                            style={{ flex: 1, minWidth: 140 }}
                        />
                        <TextField
                            label="User search"
                            variant="outlined"
                            value={draftUser}
                            onChange={(e) => setDraftUser(e.target.value)}
                            size="small"
                            fullWidth
                            style={{ flex: 1, minWidth: 140 }}
                        />
                        <TextField
                            label="Issue search"
                            variant="outlined"
                            value={draftIssue}
                            onChange={(e) => setDraftIssue(e.target.value)}
                            size="small"
                            fullWidth
                            style={{ flex: 1, minWidth: 140 }}
                        />
                    </Container>
                    <Container
                        style={{
                            flexWrap: 'wrap',
                            alignItems: 'flex-end',
                            gap: theme.spacing(1.5),
                            flexShrink: 0,
                        }}
                    >
                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{ minWidth: 140 }}
                        >
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={listFilters.category || ''}
                                onChange={(e) => {
                                    const v = e.target.value as
                                        | SupportTicketCategory
                                        | ''
                                    updateFilters({
                                        category: v,
                                        subCategory: '',
                                    })
                                }}
                                label="Category"
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {Object.values(SupportTicketCategory).map(
                                    (c) => (
                                        <MenuItem key={c} value={c}>
                                            {normalizeCategory(c)}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>
                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{ minWidth: 160 }}
                        >
                            <InputLabel>Subcategory</InputLabel>
                            <Select
                                value={listFilters.subCategory || ''}
                                onChange={(e) => {
                                    const v = e.target.value as
                                        | SupportTicketSubCategory
                                        | ''
                                    updateFilters({ subCategory: v })
                                }}
                                label="Subcategory"
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {Object.values(SupportTicketSubCategory).map(
                                    (s) => (
                                        <MenuItem key={s} value={s}>
                                            {normalizeCategory(s)}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>
                    </Container>
                </Container>

                {listError && (
                    <Container
                        style={{
                            marginBottom: theme.spacing(1),
                            alignItems: 'center',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Typography color="error">
                            Failed to load support tickets.
                        </Typography>
                        <Typography
                            color="primary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                void refetchSupportTicketList()
                            }}
                        >
                            Retry
                        </Typography>
                    </Container>
                )}

                <Container
                    style={{
                        flexDirection: 'column',
                        marginTop: theme.spacing(1),
                        maxHeight: 'calc(100vh - 220px)',
                        minHeight: 'calc(100vh - 220px)',
                        overflowY: 'auto',
                        position: 'relative',
                    }}
                >
                    {listBodyLoading && (
                        <Container
                            style={{
                                position: 'absolute',
                                inset: 0,
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1,
                                backgroundColor: theme.palette.background.paper,
                            }}
                        >
                            <EzosLoader />
                        </Container>
                    )}
                    {supportTicketList.map((ticket) => (
                        <SupportTicketRow
                            key={ticket.id}
                            ticket={ticket}
                            theme={theme}
                            handleTransition={handleTransition}
                            handleOpenMessageDrawer={handleOpenMessageDrawer}
                        />
                    ))}
                    {!listBodyLoading && supportTicketList.length === 0 && (
                        <Typography
                            color="textSecondary"
                            style={{ margin: theme.spacing(2) }}
                        >
                            No support tickets match your filters.
                        </Typography>
                    )}
                </Container>

                <Container
                    style={{
                        ...typographyStyle(theme.typography.subtitle1),
                        borderTop: `1px solid ${theme.palette.grey[400]}`,
                        padding: theme.spacing(1),
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1.5),
                    }}
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            gap: theme.spacing(2),
                            flexWrap: 'wrap',
                            flex: 1,
                            justifyContent: 'center',
                            minWidth: 0,
                        }}
                    >
                        <Pagination
                            count={numPages}
                            page={page}
                            onChange={(_: unknown, newPage: number) => {
                                updateListParams({ page: newPage })
                            }}
                        />
                        <span>
                            {totalCount > 0
                                ? `${rangeStart} - ${rangeEnd} of ${totalCount}`
                                : '0 of 0'}
                        </span>
                    </Container>
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 100, flexShrink: 0 }}
                    >
                        <InputLabel>Per page</InputLabel>
                        <Select
                            value={pageSize}
                            onChange={(e) => {
                                updateListParams({
                                    pageSize: Number(e.target.value),
                                })
                            }}
                            label="Per page"
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Container>
            </Container>
            <SideDrawer
                open={selectedTicket !== null && openMessageDrawer}
                width={DRAWER_W}
                handleClose={() => {
                    setSelectedTicket(null)
                    setOpenMessageDrawer(false)
                }}
            >
                {selectedTicket?.channel && (
                    <MessageForm
                        theme={theme}
                        channelId={selectedTicket.channel}
                        afterCreateMessage={() => {
                            if (selectedTicket) {
                                bumpMessageCountForTicket(selectedTicket.id)
                            }
                        }}
                    />
                )}
            </SideDrawer>
        </SideDrawerContainer>
    )
}
